
class MoonPhaseRenderer {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');

        // La taille INTRINSÈQUE du canevas (ses attributs width/height, ou un défaut) est sa taille
        // en pixels CSS ; la mémoire de dessin, elle, suit la densité de l'écran. Sans ce doublement
        // un disque de 30 px se dessine dans 30 pixels physiques et s'étire ensuite : le glyphe
        // sortait franchement flou sur tout écran dense, ce qui est la moitié de son travail.
        const cssSize = { w: canvas.width || 200, h: canvas.height || 200 };
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        canvas.width = Math.round(cssSize.w * dpr);
        canvas.height = Math.round(cssSize.h * dpr);
        canvas.style.width = `${cssSize.w}px`;
        canvas.style.height = `${cssSize.h}px`;
        this.ctx.scale(dpr, dpr);

        // On raisonne ensuite en pixels CSS, la mise à l'échelle étant posée une fois pour toutes.
        this.centerX = cssSize.w / 2;
        this.centerY = cssSize.h / 2;
        this.radius = Math.min(this.centerX, this.centerY) * 0.9375;

        // Painted on a canvas, so out of the stylesheet's reach: the kawaii palette is written here
        // (cf. sky-chart/core/constants.js, same reason).
        //
        // A MOON IS A LIGHT THING ON A DARK SKY, and it was drawn the other way round: pale cream on
        // pale cream, with the shadow in full lilac — the unlit side ended up the loudest thing in
        // the cell. The host now paints the night (components/_astro.scss), this draws only the moon
        // on it, and NOTHING paints a ground here: a canvas that fills its own background shows its
        // square wherever the host's colour is not exactly the one guessed here.
        this.moonColor = '#ffe9bd';
        this.craterColor = 'rgba(255, 255, 255, 0.22)';
        this.craterShadowColor = 'rgba(120, 96, 78, 0.16)';
        // Le disque NON éclairé n'est PAS peint : il est le fond, littéralement. On veut l'ombre et
        // la nuit de la même couleur, et la seule façon de le garantir est de ne rien poser dessus —
        // recopier ici le `--k-night` de la feuille donnerait deux valeurs à tenir d'accord, et un
        // jour d'écart entre les deux se verrait comme un disque plus clair que son ciel.
        //
        // Reste le LIMBE, un simple trait : il dit où est la lune quand elle n'est presque pas
        // éclairée, et fait de la nouvelle lune un cercle vide plutôt qu'une case vide. Translucide,
        // donc juste sur n'importe quel fond — il ne suppose rien de la couleur qu'il borde.
        this.limbColor = 'rgba(199, 180, 240, 0.32)';

        // Définir les cratères plus larges et plus estompés
        this.craters = [
            { x: 0.2, y: 0.3, size: 0.2 },
            { x: -0.1, y: -0.2, size: 0.14 },
            { x: 0.4, y: -0.4, size: 0.24 },
            { x: -0.3, y: 0.5, size: 0.2 },
            { x: 0.1, y: 0.1, size: 0.28 },
            { x: -0.5, y: -0.1, size: 0.18 },
            { x: 0.3, y: -0.3, size: 0.14 },
            { x: -0.2, y: 0.4, size: 0.14 }
        ];
    }

    render(options = {}) {
        // Phase angle: 0-360 degrés
        // 0 = nouvelle lune
        // 90 = premier quartier
        // 180 = pleine lune
        // 270 = dernier quartier
        // Illumination: 0-100%
        const phaseAngle = options.phaseAngle || 0;
        const illumination = Math.min(100, Math.max(0, options.illumination || 100)) / 100;

        // Transparent : le fond appartient à l'hôte (cf. le commentaire de palette). En pixels CSS,
        // comme tout ce qui suit — `canvas.width` compte désormais des pixels physiques.
        this.ctx.clearRect(0, 0, this.centerX * 2, this.centerY * 2);

        this.drawMoon(phaseAngle, illumination);
    }

    /**
     * La lune se dessine en DEUX temps : le disque entier en voile, puis la part éclairée découpée
     * dedans. C'était l'inverse — disque clair, puis une ombre posée par-dessus — et cette ombre-là
     * se refermait sur les COINS de la boîte du canevas : elle débordait donc le disque et rendait
     * un demi-rectangle violet. C'est le « carré » qu'on voyait, et il ne venait pas du fond.
     *
     * Découper plutôt que recouvrir ferme le sujet pour de bon : la lumière est bornée au disque par
     * construction, quel que soit le chemin du terminateur.
     */
    drawMoon(phaseAngle, illumination) {
        // Convertir l'angle en radians
        const phase = (phaseAngle * Math.PI / 180) % (2 * Math.PI);

        // Déterminer si la lune est croissante ou décroissante
        const isWaxing = (phase <= Math.PI);

        // Le limbe, toujours : c'est lui qui donne sa rondeur au croissant.
        this.drawLimb();

        // Nouvelle lune : le limbe seul, et rien d'autre à montrer.
        if (illumination <= 0.01) {
            return;
        }

        this.ctx.save();

        // Premier découpage : le disque. La lumière ne peut plus en sortir.
        this.ctx.beginPath();
        this.ctx.arc(this.centerX, this.centerY, this.radius, 0, Math.PI * 2);
        this.ctx.clip();

        // Second découpage : la part ÉCLAIRÉE, sauf pleine lune où elle est tout le disque.
        if (illumination < 0.99) {
            // Paramètre k contrôlant la forme du terminateur
            // k varie de -1 (nouvelle lune) à 1 (pleine lune)
            // k = 0 correspond au premier ou dernier quartier
            let k = 2 * illumination - 1;

            // Ajuster k pour les phases décroissantes
            if (isWaxing) {
                k = -k;
            }

            this.ctx.beginPath();

            // Point de départ en haut du disque
            this.ctx.moveTo(this.centerX, this.centerY - this.radius);

            // Tracer la courbe du terminateur
            const steps = 40; // Nombre de segments pour la courbe

            for (let i = 1; i <= steps; i++) {
                // Angle paramétrique de -π/2 à π/2
                const theta = (Math.PI * i / steps) - Math.PI / 2;

                // Coordonnées du point sur le terminateur
                const x = this.centerX + this.radius * k * Math.cos(theta);
                const y = this.centerY + this.radius * Math.sin(theta);

                this.ctx.lineTo(x, y);
            }

            // Refermer du côté ÉCLAIRÉ — l'opposé de l'ombre : croissante, la lumière est à droite ;
            // décroissante, à gauche (vue de l'hémisphère nord).
            const edge = isWaxing ? this.centerX + this.radius : this.centerX - this.radius;
            this.ctx.lineTo(edge, this.centerY + this.radius);
            this.ctx.lineTo(edge, this.centerY - this.radius);
            this.ctx.closePath();
            this.ctx.clip();
        }

        this.drawDisk();
        this.drawCraters();

        this.ctx.restore();
    }

    /** Le bord du disque, et rien de plus : l'intérieur non éclairé reste le fond de l'hôte. */
    drawLimb() {
        this.ctx.beginPath();
        this.ctx.arc(this.centerX, this.centerY, this.radius, 0, Math.PI * 2);
        this.ctx.lineWidth = 1;
        this.ctx.strokeStyle = this.limbColor;
        this.ctx.stroke();
    }

    /**
     * Le disque ÉCLAIRÉ, avec son léger dégradé pour le relief. Il prenait une couleur en paramètre
     * et portait un second dégradé, sombre, pour la part dans l'ombre ; celle-ci est un voile
     * maintenant, et une branche qui ne servait plus qu'à se comparer à sa propre constante
     * (`color === this.moonColor`) n'avait plus lieu d'être.
     */
    drawDisk() {
        const gradient = this.ctx.createRadialGradient(
            this.centerX - this.radius * 0.2,
            this.centerY - this.radius * 0.2,
            0,
            this.centerX,
            this.centerY,
            this.radius
        );
        gradient.addColorStop(0, '#fff8ea');
        gradient.addColorStop(0.75, this.moonColor);
        gradient.addColorStop(1, '#ffc65c');

        this.ctx.beginPath();
        this.ctx.arc(this.centerX, this.centerY, this.radius, 0, Math.PI * 2);
        this.ctx.fillStyle = gradient;
        this.ctx.fill();
    }

    drawCraters() {
        // Dessiner les cratères avec un léger effet 3D
        for (const crater of this.craters) {
            const x = this.centerX + crater.x * this.radius;
            const y = this.centerY + crater.y * this.radius;
            const size = crater.size * this.radius;

            // Ombre du cratère
            this.ctx.beginPath();
            this.ctx.arc(x + size * 0.2, y + size * 0.2, size, 0, Math.PI * 2);
            this.ctx.fillStyle = this.craterShadowColor;
            this.ctx.fill();

            // Cratère principal
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fillStyle = this.craterColor;
            this.ctx.fill();
        }
    }
}

// Dessine le glyphe de phase à partir des data-* (phase-angle, illumination) d'un élément.
// Aucune logique astro côté client : la donnée est calculée par le moteur (Orekit) et posée
// server-side (contributeur calendrier / attributs data-*). Le canvas ne fait qu'hydrater.
function renderMoonInto(host, appendCanvas) {
    const target = appendCanvas ? document.createElement('canvas') : host;
    if (appendCanvas) {
        target.className = 'moon';
        target.width = 24;
        target.height = 24;
    }
    new MoonPhaseRenderer(target).render({
        phaseAngle: parseFloat(host.dataset.phaseAngle || 0),
        illumination: parseFloat(host.dataset.illumination || 100),
    });
    if (appendCanvas) {
        host.appendChild(target);
        // Les glyphes sont petits : un label chiffré rend la différence entre jours proches lisible.
        const label = document.createElement('span');
        label.className = 'label';
        label.textContent = Math.round(parseFloat(host.dataset.illumination || 0)) + ' %';
        host.appendChild(label);
    }
}

document.addEventListener("DOMContentLoaded", function () {
    // Canvas lune explicites (data-* portés directement par le <canvas>).
    document.querySelectorAll("canvas.moon").forEach(canvas => renderMoonInto(canvas, false));

    // Marqueurs du calendrier d'archive : chaque jour porte ses data-* (posés server-side) ; on
    // hydrate un petit canvas dedans. Zéro fetch.
    document.querySelectorAll('.ui-calendar-day .plugin.moon').forEach(div => renderMoonInto(div, true));
});